import { useState, useEffect } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Link } from '@evertrue/et-components';
import Loading from 'components/elements/loading';
import GlobalSearchResultsModule from './components/global-search-results-module';
import SimpleContactsTable from './components/simple-contacts-table';
import InteractionUtils from 'apps/interactions/interaction-utils';
import StringSearchDisplay from 'components/formatting/string-search-display';
import EmptyStateMessage from 'components/lists/empty-state-message';
import Api from 'entities/helpers/api';
import { getQueryTemplate } from './config/global-search-config';
import InteractionTable from 'apps/interactions/components/interaction-table';

const CONTACT_LIMIT = 25;
const INTERACTION_LIMIT = 10;

const GlobalSearchResultsController = ({ query = '', type = 'all' }) => {
  const [contactResults, setContactResults] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);
  const [contactOffset, setContactOffset] = useState(0);
  const [contactPage, setContactPage] = useState(1);

  const [interactionResults, setInteractionResults] = useState([]);
  const [interactionTotal, setInteractionTotal] = useState(0);
  const [interactionOffset, setInteractionOffset] = useState(0);
  const [interactionPage, setInteractionPage] = useState(1);

  const [loading, setLoading] = useState(true); //always load on mount

  useEffect(() => {
    setLoading(true);
    setContactResults([]);
    setContactTotal(0);
    setContactOffset(0);
    setContactPage(1);
    setInteractionResults([]);
    setInteractionTotal(0);
    setInteractionOffset(0);
    setInteractionPage(1);
  }, [query, type]);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        if (type === 'interaction' || type === 'all') {
          const interactionResponse = await Api.SEARCH.CONTACT_NOTE.post({
            data: JSON.stringify(getQueryTemplate(query, 'interaction')),
            params: { limit: INTERACTION_LIMIT, offset: interactionOffset },
          });
          setInteractionResults(interactionResponse.items);
          setInteractionTotal(interactionResponse.total);
        }
        if (type !== 'interaction') {
          const contactResponse = await Api.CONTACTS.SEARCH.post({
            data: JSON.stringify(getQueryTemplate(query, type)),
            params: { limit: CONTACT_LIMIT, offset: contactOffset },
          });
          setContactResults(contactResponse.items);
          setContactTotal(contactResponse.total);
        }
      } catch (error) {
        EverTrue.Alert.error('Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [query, type, contactOffset, contactPage, interactionOffset, interactionPage]);

  if (loading) {
    return (
      <div className="global-autocomplete-results--loading">
        <Loading />
      </div>
    );
  }

  const resultsTotal =
    type === 'all' ? contactTotal + interactionTotal : type === 'interaction' ? interactionTotal : contactTotal;

  const handleInteractionPageChange = (newPage) => {
    setInteractionOffset((newPage - 1) * INTERACTION_LIMIT);
    setInteractionPage(newPage);
  };

  const renderInteractionModule = () => {
    return (
      <GlobalSearchResultsModule
        label={'Interactions'}
        total={interactionTotal}
        query={query}
        offset={interactionOffset}
        limit={INTERACTION_LIMIT}
        queryType={type}
      >
        <InteractionTable
          notes={interactionResults}
          highlight={InteractionUtils.getHighlightRegex(query)}
          showContact
          loading={loading}
          total={interactionTotal}
          limit={INTERACTION_LIMIT}
          page={interactionPage}
          onPageChange={handleInteractionPageChange}
        />
      </GlobalSearchResultsModule>
    );
  };

  const renderContactModule = () => {
    return (
      <GlobalSearchResultsModule
        label={'Contacts'}
        total={contactTotal}
        query={query}
        page={contactPage}
        setOffset={setContactOffset}
        setPage={setContactPage}
        offset={contactOffset}
        limit={CONTACT_LIMIT}
        queryType={type}
      >
        <SimpleContactsTable contacts={contactResults} queryType={type} query={query} />
      </GlobalSearchResultsModule>
    );
  };

  return (
    <div className="global-autocomplete-results">
      <div className="global-autocomplete-results--details">
        <strong>{numeral(resultsTotal).format('0,0')}</strong>
        {' results matching '}
        <strong>{type}</strong>
        {' for '}
        <StringSearchDisplay string={query} />
      </div>

      {resultsTotal === 0 && !loading ? (
        <div className="global-autocomplete-results--empty">
          <EmptyStateMessage
            text={
              <span>
                {'No results found for '}
                <StringSearchDisplay string={query} />
                <div className="global-autocomplete-results--browse-link">
                  <Link title="Return to Browse" href="/contact">
                    Return to Browse
                  </Link>
                </div>
              </span>
            }
          />
        </div>
      ) : (
        <div className="global-autocomplete-results--content">
          {type === 'all' ? (
            <>
              {renderContactModule()}
              {renderInteractionModule()}
            </>
          ) : type === 'interaction' ? (
            renderInteractionModule()
          ) : (
            renderContactModule()
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchResultsController;

GlobalSearchResultsController.propTypes = {
  query: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};
